import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

// Constants
const GRID_SIZE = 9;
const BOX_SIZE = 3;
const MAX_HISTORY = 100;

// Pure helper functions (no dependencies)
const createEmptyGrid = () => Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(0));
const createEmptyPencilMarks = () => Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(null).map(() => []));
const copyGrid = (grid) => grid.map(row => [...row]);
const copyPencilMarks = (marks) => marks.map(row => row.map(cell => [...cell]));

const getBoxStart = (index) => Math.floor(index / BOX_SIZE) * BOX_SIZE;

const isValidInUnit = (grid, row, col, value, checkFn) => {
  for (let i = 0; i < GRID_SIZE; i++) {
    const [checkRow, checkCol] = checkFn(i);
    if ((checkRow !== row || checkCol !== col) && grid[checkRow][checkCol] === value) {
      return false;
    }
  }
  return true;
};

const isValidPlacement = (grid, row, col, value) => {
  if (!isValidInUnit(grid, row, col, value, (i) => [row, i])) return false;
  if (!isValidInUnit(grid, row, col, value, (i) => [i, col])) return false;
  const boxRow = getBoxStart(row);
  const boxCol = getBoxStart(col);
  for (let r = boxRow; r < boxRow + BOX_SIZE; r++) {
    for (let c = boxCol; c < boxCol + BOX_SIZE; c++) {
      if ((r !== row || c !== col) && grid[r][c] === value) return false;
    }
  }
  return true;
};

const findErrors = (grid) => {
  const errors = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      const value = grid[row][col];
      if (value !== 0 && !isValidPlacement(grid, row, col, value)) {
        errors.push([row, col]);
      }
    }
  }
  return errors;
};

// Remove candidate from related cells when a value is placed.
// Exported so external UI code (e.g. the walkthrough panel in GamePage)
// can build a projected pencil-mark grid without going through
// setCellValue — the walkthrough places values in a *shadow* grid and
// needs the corresponding candidate cleanup applied to a shadow marks
// grid without mutating the user's real state.
export const removeCandidateFromRelated = (marks, row, col, value) => {
  const newMarks = copyPencilMarks(marks);
  newMarks[row][col] = [];
  
  for (let i = 0; i < GRID_SIZE; i++) {
    newMarks[row][i] = newMarks[row][i].filter(v => v !== value);
    newMarks[i][col] = newMarks[i][col].filter(v => v !== value);
  }
  
  const boxRow = getBoxStart(row);
  const boxCol = getBoxStart(col);
  for (let r = boxRow; r < boxRow + BOX_SIZE; r++) {
    for (let c = boxCol; c < boxCol + BOX_SIZE; c++) {
      newMarks[r][c] = newMarks[r][c].filter(v => v !== value);
    }
  }
  
  return newMarks;
};

// Restore candidates that become valid when a value is removed from a cell
const restoreCandidatesAfterErase = (grid, marks, row, col, erasedValue) => {
  const newMarks = copyPencilMarks(marks);
  
  // For each cell in the same row/col/box that previously had this candidate removed,
  // check if the candidate is now valid and the cell had OTHER candidates (was being tracked)
  const cellsToCheck = new Set();
  
  // Row cells
  for (let i = 0; i < GRID_SIZE; i++) {
    if (i !== col) cellsToCheck.add(`${row},${i}`);
  }
  // Column cells
  for (let i = 0; i < GRID_SIZE; i++) {
    if (i !== row) cellsToCheck.add(`${i},${col}`);
  }
  // Box cells
  const boxRow = getBoxStart(row);
  const boxCol = getBoxStart(col);
  for (let r = boxRow; r < boxRow + BOX_SIZE; r++) {
    for (let c = boxCol; c < boxCol + BOX_SIZE; c++) {
      if (r !== row || c !== col) cellsToCheck.add(`${r},${c}`);
    }
  }
  
  cellsToCheck.forEach(key => {
    const [r, c] = key.split(',').map(Number);
    // Only restore to empty cells that have existing pencil marks (were being tracked)
    if (grid[r][c] === 0 && newMarks[r][c].length > 0) {
      if (!newMarks[r][c].includes(erasedValue) && isValidPlacement(grid, r, c, erasedValue)) {
        newMarks[r][c] = [...newMarks[r][c], erasedValue].sort((a, b) => a - b);
      }
    }
  });
  
  return newMarks;
};

const calculateAllCandidates = (grid) => {
  const marks = createEmptyPencilMarks();
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (grid[row][col] === 0) {
        for (let num = 1; num <= GRID_SIZE; num++) {
          if (isValidPlacement(grid, row, col, num)) {
            marks[row][col].push(num);
          }
        }
      }
    }
  }
  return marks;
};

export function useSudokuGame() {
  const [grid, setGrid] = useState(createEmptyGrid);
  const [pencilMarks, setPencilMarks] = useState(createEmptyPencilMarks);
  const [errors, setErrors] = useState([]);
  // wrongCells is populated ONLY when the user explicitly runs "Check
  // My Work" — it's a solution-based diff, not the auto-computed
  // internal-consistency check that lives in `errors`. Any user edit
  // to a flagged cell removes it from this list automatically so the
  // red highlight tracks the user's fix attempts in real time.
  const [wrongCells, setWrongCells] = useState([]);
  const originalGridRef = useRef(createEmptyGrid());

  // Latest-committed-state mirrors. These are updated in a useEffect
  // AFTER React commits, so any handler (like clearWrongCells) can
  // grab the truly-current grid/marks without going through a
  // functional updater. Functional updaters are double-invoked in
  // React.StrictMode, which caused pushHistory to fire twice per call
  // and left the undo stack with duplicate/mis-ordered snapshots.
  const gridRef = useRef(grid);
  const marksRef = useRef(pencilMarks);
  useEffect(() => { gridRef.current = grid; }, [grid]);
  useEffect(() => { marksRef.current = pencilMarks; }, [pencilMarks]);

  // Undo/Redo history
  const historyRef = useRef([]);
  const redoStackRef = useRef([]);

  const pushHistory = useCallback((gridSnap, marksSnap) => {
    historyRef.current.push({
      grid: copyGrid(gridSnap),
      marks: copyPencilMarks(marksSnap),
    });
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift();
    }
    // Any new action clears redo stack
    redoStackRef.current = [];
  }, []);

  const initializeGrid = useCallback((newGrid) => {
    const gridCopy = copyGrid(newGrid);
    originalGridRef.current = gridCopy;
    setGrid(copyGrid(newGrid));
    setPencilMarks(createEmptyPencilMarks());
    setErrors([]);
    setWrongCells([]);
    historyRef.current = [];
    redoStackRef.current = [];
  }, []);

  const setCellValue = useCallback((row, col, value) => {
    if (originalGridRef.current[row][col] !== 0) return false;

    // Any edit to a flagged-wrong cell clears its red highlight — the
    // user is actively responding to the feedback.
    setWrongCells(prev => prev.filter(([r, c]) => !(r === row && c === col)));

    // Save current state to history before modifying
    setGrid(prevGrid => {
      setPencilMarks(prevMarks => {
        pushHistory(prevGrid, prevMarks);
        
        const newGrid = copyGrid(prevGrid);
        const oldValue = newGrid[row][col];
        newGrid[row][col] = value;
        setErrors(findErrors(newGrid));
        
        let newMarks;
        if (value !== 0) {
          // Placing a value: remove candidate from related cells
          newMarks = removeCandidateFromRelated(prevMarks, row, col, value);
        } else if (oldValue !== 0) {
          // Erasing a value: restore candidates that become valid again
          newMarks = restoreCandidatesAfterErase(newGrid, prevMarks, row, col, oldValue);
        } else {
          newMarks = prevMarks;
        }
        
        // Update grid via outer setState return
        setGrid(newGrid);
        return newMarks;
      });
      return prevGrid; // initial return, overridden by inner setGrid
    });

    return true;
  }, [pushHistory]);

  const togglePencilMark = useCallback((row, col, value) => {
    if (originalGridRef.current[row][col] !== 0) return false;
    
    setGrid(currentGrid => {
      if (currentGrid[row][col] !== 0) return currentGrid;
      
      setPencilMarks(prevMarks => {
        pushHistory(currentGrid, prevMarks);
        
        const newMarks = copyPencilMarks(prevMarks);
        const marks = newMarks[row][col];
        
        if (marks.includes(value)) {
          newMarks[row][col] = marks.filter(v => v !== value);
        } else {
          newMarks[row][col] = [...marks, value].sort((a, b) => a - b);
        }
        return newMarks;
      });
      
      return currentGrid;
    });

    return true;
  }, [pushHistory]);

  // Undo / Redo — ref-based so history stack mutations happen exactly
  // ONCE per user click. Previous implementation used functional state
  // updaters; React.StrictMode's double-invoke of updaters caused the
  // pop/push side effects to run twice, effectively popping two history
  // entries per Undo. See iteration_42 test report for evidence.
  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return false;
    // Push current committed state onto redo stack.
    redoStackRef.current.push({
      grid: copyGrid(gridRef.current),
      marks: copyPencilMarks(marksRef.current),
    });
    // Pop the previous state and restore.
    const prev = historyRef.current.pop();
    setGrid(prev.grid);
    setPencilMarks(prev.marks);
    setErrors(findErrors(prev.grid));
    // Any red highlights are stale after a state jump.
    setWrongCells([]);
    return true;
  }, []);

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return false;
    historyRef.current.push({
      grid: copyGrid(gridRef.current),
      marks: copyPencilMarks(marksRef.current),
    });
    const next = redoStackRef.current.pop();
    setGrid(next.grid);
    setPencilMarks(next.marks);
    setErrors(findErrors(next.grid));
    setWrongCells([]);
    return true;
  }, []);

  const canUndo = useCallback(() => historyRef.current.length > 0, []);
  const canRedo = useCallback(() => redoStackRef.current.length > 0, []);

  const autoFillCandidates = useCallback(() => {
    setGrid(currentGrid => {
      setPencilMarks(prevMarks => {
        pushHistory(currentGrid, prevMarks);
        return calculateAllCandidates(currentGrid);
      });
      return currentGrid;
    });
  }, [pushHistory]);

  const resetGame = useCallback(() => {
    setGrid(prevGrid => {
      setPencilMarks(prevMarks => {
        pushHistory(prevGrid, prevMarks);
        return createEmptyPencilMarks();
      });
      return copyGrid(originalGridRef.current);
    });
    setErrors([]);
  }, [pushHistory]);

  // "Clear all wrong entries" — one-click reversal of every cell the
  // user's most recent Check-My-Work flagged as wrong. Records ONE
  // history entry so a single Undo restores them all.
  //
  // Reads state from refs (updated by useEffect after each commit) so
  // pushHistory is called EXACTLY ONCE, synchronously, outside any
  // functional updater. This bypasses React.StrictMode's double-invoke
  // of updaters that was corrupting the undo stack in prior attempts.
  const clearWrongCells = useCallback(() => {
    if (wrongCells.length === 0) return;
    const currentGrid = gridRef.current;
    const currentMarks = marksRef.current;
    // Snapshot the truly-current state ONCE, outside any updater.
    pushHistory(currentGrid, currentMarks);
    const newGrid = copyGrid(currentGrid);
    let newMarks = currentMarks;
    for (const [r, c] of wrongCells) {
      const oldValue = newGrid[r][c];
      if (oldValue !== 0) {
        newGrid[r][c] = 0;
        newMarks = restoreCandidatesAfterErase(newGrid, newMarks, r, c, oldValue);
      }
    }
    setGrid(newGrid);
    setPencilMarks(newMarks);
    setErrors(findErrors(newGrid));
    setWrongCells([]);
  }, [wrongCells, pushHistory]);

  const isComplete = useMemo(() => {
    return grid.every(row => row.every(cell => cell !== 0)) && errors.length === 0;
  }, [grid, errors]);

  const originalGrid = originalGridRef.current;

  return {
    grid,
    originalGrid,
    pencilMarks,
    errors,
    wrongCells,
    setWrongCells,
    clearWrongCells,
    initializeGrid,
    setCellValue,
    togglePencilMark,
    autoFillCandidates,
    resetGame,
    isComplete,
    undo,
    redo,
    canUndo,
    canRedo,
  };
}
